import { chatAPI } from './api';

/**
 * Service worker y notificaciones push de la app movil.
 *
 * El service worker (public/sw.js) cubre toda la web (scope '/'), no solo
 * /tecnicos: el gestor tambien usa la app (chats y el gestor completo) y
 * `navigator.serviceWorker.ready` no se resuelve nunca en una pagina fuera del
 * alcance del service worker, con lo que la suscripcion a los avisos se
 * quedaria esperando para siempre en /chats.
 *
 * Las notificaciones llegan aunque la app este cerrada, siempre que se haya
 * concedido el permiso. El permiso solo se puede pedir tras un gesto del
 * usuario (un boton), nunca al cargar la pagina.
 */

export const PUSH_CHANGED_EVENT = 'gpi-push-changed';

const SW_URL = '/sw.js';
const SCOPE = '/';

// iPhone: las notificaciones solo existen en la web instalada en la pantalla de
// inicio (iOS 16.4 o superior). En una pestana de Safari no hay ni PushManager.
export const isIos = () =>
  typeof navigator !== 'undefined'
  && (/iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export const isStandalone = () =>
  typeof window !== 'undefined'
  && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

export const pushSupported = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    // La primera version lo registraba solo para /tecnicos: se retira, o el
    // movil tendria dos registros y dos suscripciones al mismo servidor
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations
      .filter((r) => new URL(r.scope).pathname !== SCOPE)
      .map((r) => r.unregister()));
    return await navigator.serviceWorker.register(SW_URL, { scope: SCOPE });
  } catch (err) {
    console.warn('No se ha podido registrar el service worker', err);
    return null;
  }
}

function base64ToBytes(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const sameBytes = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

// Permiso de push segun el propio PushManager: 'granted', 'denied' o 'prompt'.
// Notification.permission no es de fiar en la app de Android (TWA): ahi el permiso
// lo gestiona la propia app y el valor que ve la pagina puede ir por detras de lo
// que el sistema permite de verdad. Lo que cuenta es poder suscribirse.
async function pushPermissionState(registration) {
  try {
    return await registration.pushManager.permissionState({ userVisibleOnly: true });
  } catch (err) {
    return { granted: 'granted', denied: 'denied' }[Notification.permission] || 'prompt';
  }
}

const readyWithTimeout = (ms = 6000) => Promise.race([
  navigator.serviceWorker.ready,
  new Promise((resolve) => setTimeout(() => resolve(null), ms)),
]);

/**
 * Da de alta (o refresca) la suscripcion de este movil en el servidor.
 *
 * Sin `force` (cada arranque) solo actua si el permiso ya esta dado: el navegador
 * puede rotar el endpoint, y si otra persona entra en el mismo movil la
 * suscripcion tiene que pasar a ser suya. Con `force` (el usuario ha pulsado
 * Activar) intenta suscribirse siempre, sin fiarse de Notification.permission, y
 * si falla lanza el error tal cual para poder mostrarlo.
 */
export async function syncPushSubscription({ force = false } = {}) {
  if (!pushSupported()) return false;
  const registration = await navigator.serviceWorker.ready;
  if (!force && (await pushPermissionState(registration)) !== 'granted') return false;

  const { data } = await chatAPI.pushKey();
  const serverKey = base64ToBytes(data.public_key);

  let subscription = await registration.pushManager.getSubscription();
  // Si la clave del servidor ha cambiado, la suscripcion vieja ya no vale
  const currentKey = subscription?.options?.applicationServerKey;
  if (subscription && currentKey && !sameBytes(new Uint8Array(currentKey), serverKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: serverKey,
    });
  }
  const json = subscription.toJSON();
  await chatAPI.pushSubscribe({
    endpoint: json.endpoint, keys: json.keys, user_agent: navigator.userAgent,
  });
  // Avisa a quien muestre el estado (PushBanner): la suscripcion se crea sola en
  // el arranque, un instante despues de que el aviso haya mirado el estado
  window.dispatchEvent(new Event(PUSH_CHANGED_EVENT));
  return true;
}

/**
 * Estado real de los avisos en ESTE movil:
 *   unsupported | ios-install | denied | inactive | active
 * "Activo" es tener una suscripcion que nada marca como bloqueada. No se exige
 * que algun indicador diga 'granted': en la app de Android los dos pueden seguir
 * diciendo 'default'/'prompt' con la suscripcion hecha, y entonces el aviso de
 * Activar no desaparecia nunca. Si de verdad no se pueden mostrar, lo dice la
 * prueba de notificaciones (el service worker informa del error al mostrarla).
 */
export async function getPushStatus() {
  if (isIos() && !isStandalone()) return { state: 'ios-install' };
  if (!pushSupported()) return { state: 'unsupported' };
  const registration = await readyWithTimeout();
  if (!registration) return { state: 'inactive', permission: Notification.permission, pushState: 'sin service worker' };
  const pushState = await pushPermissionState(registration);
  const subscription = await registration.pushManager.getSubscription().catch(() => null);
  const permission = Notification.permission;
  let state = 'inactive';
  if (pushState === 'denied' || permission === 'denied') state = 'denied';
  else if (subscription) state = 'active';
  return { state, permission, pushState, hasSubscription: !!subscription };
}

/** Datos tecnicos para la pantalla de prueba: lo que hay que mirar si algo falla. */
export async function pushDiagnostics() {
  const lines = [];
  const add = (k, v) => lines.push(`${k}: ${v}`);
  const ua = navigator.userAgent || '';
  const chrome = (ua.match(/(Chrome|CriOS|Safari)\/[\d.]+/) || [''])[0];
  add('Navegador', chrome || ua.slice(0, 60));
  add('Modo', isStandalone() ? 'app instalada' : 'pestana del navegador');
  add('Abierta desde', document.referrer || '(directa)');
  if (!pushSupported()) {
    add('Notificaciones', 'no soportadas');
    return lines;
  }
  add('Notification.permission', Notification.permission);
  const registration = await readyWithTimeout(3000);
  if (!registration) {
    add('Service worker', 'no esta listo');
    return lines;
  }
  add('Permiso de push', await pushPermissionState(registration));
  const subscription = await registration.pushManager.getSubscription().catch(() => null);
  add('Suscripcion', subscription ? `si (${new URL(subscription.endpoint).hostname})` : 'no');
  return lines;
}

/**
 * Activa los avisos (desde un boton). Devuelve { ok, error }: si falla, `error`
 * lleva el nombre y mensaje reales del navegador, que es lo que hace falta para
 * saber por que.
 */
export async function enablePush() {
  if (!pushSupported()) return { ok: false, error: 'Este navegador no admite notificaciones' };
  try {
    await Notification.requestPermission();
  } catch (err) {
    // Algunos navegadores lanzan en vez de devolver 'denied': se intenta suscribir igual
  }
  try {
    await syncPushSubscription({ force: true });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: `${err.name || 'Error'}: ${err.message}` };
  }
}

/**
 * Al salir de la app: el servidor deja de avisar a este movil. Se hace antes de
 * borrar el token, que es lo que autoriza la llamada.
 */
export async function forgetPushSubscription() {
  try {
    if (!pushSupported()) return;
    const registration = await navigator.serviceWorker.getRegistration(SCOPE);
    const subscription = registration && await registration.pushManager.getSubscription();
    if (subscription) await chatAPI.pushUnsubscribe(subscription.endpoint);
  } catch (err) {
    // Sin red al salir no pasa nada grave: la suscripcion se reasigna al
    // siguiente que entre en este movil.
  }
}

/** Quita las notificaciones de un chat al abrirlo, como hace WhatsApp. */
export async function clearChatNotifications(projectId) {
  try {
    if (!pushSupported()) return;
    const registration = await navigator.serviceWorker.getRegistration(SCOPE);
    if (!registration) return;
    const list = await registration.getNotifications({ tag: `chat-${projectId}` });
    list.forEach((n) => n.close());
  } catch (err) {
    // Sin soporte de getNotifications: se quedan hasta que el usuario las quite
  }
}

/** Numero de no leidos en el icono de la app, donde el sistema lo soporte. */
export function setAppBadge(count) {
  try {
    if (!navigator.setAppBadge) return;
    if (count > 0) navigator.setAppBadge(count);
    else navigator.clearAppBadge();
  } catch (err) {
    // Opcional: si el sistema no lo admite, no hay contador en el icono
  }
}
